import { useEffect, useState, type ReactNode } from 'react';
import {
  BusSearchBar,
  busRecentStops,
  type BusSearchCopy,
  resolvePolicyTraveler,
  useTravelerFieldSearch,
  withPhotos,
  type TravelerFieldSearch,
  type BusTravelersCopy,
  type BusSearchValue,
  type BusTripType,
  type DateRange,
} from '@voll-intelligence/intelligence-ui-kit';
import {
  formatDateRange,
  formatWeekdayDayMonth,
  parseDateOnly,
  toDateOnly,
} from '@voll-intelligence/dates';
import { useGenUiConfig } from '../../../shared/ui/config.js';
import { useGenUiI18n } from '../../../shared/ui/i18n.js';
import type {
  TravelerRef,
  BusPlannerBlock,
  BusPlannerSearch,
} from '@voll-intelligence/genui-contract';
import { useBusStopsPort, type BusStopsPort } from '../../shared/bus_stops_port.js';

type I18n = ReturnType<typeof useGenUiI18n>;

/** "Ter, 25 ago" — vazio quando o dia é ilegível. */
function weekdayDay(iso: string, locale: string): string {
  const day = parseDateOnly(iso);
  return day ? formatWeekdayDayMonth(day, locale, new Date()) : '';
}

/** "25–27 ago" na ida e volta; "Ter, 25 ago" só ida; vazio sem ida. */
export function busWhenLabel({ from, to }: DateRange, locale: string): string {
  const outward = from ? parseDateOnly(from) : null;
  if (!outward) return '';
  const back = to ? parseDateOnly(to) : null;
  return back ? formatDateRange(outward, back, locale, new Date()) : weekdayDay(from ?? '', locale);
}

/** A copy do Quem: os textos do seletor do voo (`travelers.*`), com a pergunta
 *  do campo ("Quem") como rótulo. */
function travelersCopy({ t }: I18n): BusTravelersCopy {
  return {
    label: t('busSearch.who'),
    searchPlaceholder: t('travelers.searchPlaceholder'),
    empty: t('travelers.empty'),
    error: t('travelers.searchError'),
    loadMore: t('travelers.loadMore'),
    you: t('travelers.you'),
    youRole: t('travelers.travelerRole'),
    selectedGroup: t('travelers.selectedGroup'),
    maxReached: t('travelers.maxReached', { max: BUS_MAX_TRAVELERS }),
    minReached: t('travelers.minReached'),
    countLabel: (count) =>
      t(count === 1 ? 'travelers.count_one' : 'travelers.count_other', { count }),
    backLabel: t('travelers.back'),
    policyLabel: t('travelers.policyLabel'),
    inSearchLabel: (count) =>
      t(count === 1 ? 'travelers.inSearch_one' : 'travelers.inSearch_other', { count }),
    doneLabel: t('travelers.done'),
  };
}

/** A copy da barra da bancada, no catálogo da conversa (`busSearch.*`). */
function plannerCopy(i18n: I18n): BusSearchCopy {
  const { t, locale } = i18n;
  const when = (range: DateRange) => busWhenLabel(range, locale);
  return {
    travelers: travelersCopy(i18n),
    originLabel: t('busSearch.origin'),
    destinationLabel: t('busSearch.destination'),
    originPlaceholder: t('busSearch.originPlaceholder'),
    destinationPlaceholder: t('busSearch.destinationPlaceholder'),
    departingFromLabel: (origin) => t('busSearch.departingFrom', { origin }),
    noStopsLabel: t('busSearch.noStops'),
    stopErrorLabel: t('busSearch.stopError'),
    stopPromptLabel: t('busSearch.stopPrompt'),
    searchLabel: t('busSearch.submit'),
    backLabel: t('busSearch.back'),
    swapLabel: t('busSearch.swap'),
    tripTypeLabels: {
      roundTrip: t('busSearch.tripType.roundTrip'),
      oneWay: t('busSearch.tripType.oneWay'),
    },
    calendarLocale: locale,
    previousMonthLabel: t('busSearch.previousMonth'),
    nextMonthLabel: t('busSearch.nextMonth'),
    whenLabel: t('busSearch.when'),
    whenPlaceholder: t('busSearch.toBeDefined'),
    whenSummary: (range) => when(range) || t('busSearch.toBeDefined'),
    whenDraftSummary: (range) => {
      if (!range.from) return t('busSearch.chooseRoundTrip');
      if (!range.to) return t('busSearch.returnPending', { date: weekdayDay(range.from, locale) });
      return `${weekdayDay(range.from, locale)} → ${weekdayDay(range.to, locale)}`;
    },
    confirmWhenLabel: (range) =>
      range.from
        ? t('busSearch.confirmDates', { range: when(range) })
        : t('busSearch.confirmDatesSheet'),
    confirmWhenSheetLabel: t('busSearch.confirmDatesSheet'),
    confirmAndSearchLabel: t('busSearch.confirmAndSearch'),
  };
}

/** O que o agente já sabia, na forma da barra. Sem o Quem semeado (blocos
 *  antigos), quem viaja é quem conversa. */
function initialValue(ui: BusPlannerBlock): BusSearchValue {
  const prefill = ui.prefill ?? {};
  return {
    travelers: prefill.travelers ?? [],
    ...(prefill.policyTravelerId ? { policyTravelerId: prefill.policyTravelerId } : {}),
    ...(prefill.from ? { from: prefill.from } : {}),
    ...(prefill.to ? { to: prefill.to } : {}),
    ...(prefill.outwardDate ? { outwardDate: prefill.outwardDate } : {}),
    ...(prefill.returnDate ? { returnDate: prefill.returnDate } : {}),
  };
}

/** O teto do Quem — o mesmo de `BusPlannerSearchSchema.travelers`. */
const BUS_MAX_TRAVELERS = 9;

type RecentStops = ReturnType<typeof busRecentStops>;

/** As buscas recentes, lidas uma vez quando o card está vivo. Falha = sem
 *  recentes: o campo pede o termo, como antes. */
function useRecentStops(port: BusStopsPort | null, live: boolean, group: string) {
  const [recent, setRecent] = useState<RecentStops | undefined>();
  useEffect(() => {
    const read = port?.recentSearches;
    if (!live || !read) return undefined;
    let current = true;
    read().then(
      (searches) => {
        if (current) setRecent(busRecentStops(searches, group));
      },
      () => undefined,
    );
    return () => {
      current = false;
    };
  }, [port, live, group]);
  return recent;
}

/**
 * O Quem do card pelo port `travelers` do host — o mesmo do voo, pela busca do
 * QUEM do ônibus do kit: o solicitante fixo no topo ("Você", do bloco semeado)
 * e a busca paginada do roster. `search` ausente quando o bloco não semeia o Quem.
 */
function useCardTravelerSearch(
  ui: BusPlannerBlock,
  travelers: readonly TravelerRef[],
  live: boolean,
): { search: TravelerFieldSearch | undefined; photos: ReadonlyMap<string, string> } {
  const seeded = ui.prefill?.travelers;
  const who = useTravelerFieldSearch({
    live: live && seeded !== undefined,
    directory: useGenUiConfig().ports?.travelers,
    requester: seeded?.find((traveler) => traveler.id === ui.requesterId),
    travelers,
  });
  return seeded ? who : { search: undefined, photos: who.photos };
}

/** Quem viaja, para o eco da busca: os `{id, name}` crus e a política sempre
 *  de um deles. Sem o Quem no card, nada — viaja quem conversa. */
function travelersOf(
  value: BusSearchValue,
  requesterId: string | undefined,
): Pick<BusPlannerSearch, 'travelers' | 'policyTravelerId'> {
  if (value.travelers.length === 0) return {};
  const travelers = value.travelers.map(({ id, name }) => ({ id, name }));
  const policyTravelerId = resolvePolicyTraveler(travelers, value.policyTravelerId, requesterId);
  return { travelers, ...(policyTravelerId ? { policyTravelerId } : {}) };
}

/** A aba inicial: ida e volta, salvo quando o agente já sabe da ida sem volta. */
function initialTripType(ui: BusPlannerBlock): BusTripType {
  const prefill = ui.prefill ?? {};
  return prefill.outwardDate && !prefill.returnDate ? 'one_way' : 'round_trip';
}

/** Os termos ditos e não resolvidos, para os campos vazios da barra. */
function stopDraftsOf(ui: BusPlannerBlock): { stopDrafts?: { from?: string; to?: string } } {
  const { fromTerm, toTerm } = ui.prefill ?? {};
  if (!fromTerm && !toTerm) return {};
  return {
    stopDrafts: { ...(fromTerm ? { from: fromTerm } : {}), ...(toTerm ? { to: toTerm } : {}) },
  };
}

/**
 * O card de busca de ônibus da conversa (`bus_planner`): a `BusSearchBar` da
 * bancada num card, com as abas "Ida e volta / Somente ida", o Quem sob elas
 * (com o solicitante semeado), o inversor entre as paradas, as paradas
 * recentes no campo aberto e a lupa no fim da fileira (frame 01), e o
 * calendário (frame 02). Na ida e volta a busca pede as duas datas. O Buscar
 * devolve quem viaja, as rodoviárias (código e nome) e os dias (`onSearch`).
 * Card congelado: a barra fica travada.
 *
 * O preenchimento guiado é o do card de voo e de hotel: só no card que ainda é
 * o último da conversa (superado, o guia abriria campo no meio da thread), e o
 * card remontado do histórico não abre nada ao nascer.
 */
export function BusPlannerCard({
  ui,
  note,
  readOnly = false,
  superseded = false,
  replay = false,
  onSearch,
}: Readonly<{
  ui: BusPlannerBlock;
  note?: string;
  readOnly?: boolean;
  superseded?: boolean;
  replay?: boolean;
  onSearch: (search: BusPlannerSearch) => void;
}>): ReactNode {
  const i18n = useGenUiI18n();
  const { renderMarkdown } = useGenUiConfig();
  const port = useBusStopsPort();
  const [value, setValue] = useState(() => initialValue(ui));
  const [tripType, setTripType] = useState(() => initialTripType(ui));
  // "Hoje" do calendário, lido uma vez ao montar o card.
  const [today] = useState(() => toDateOnly(new Date()));
  const recentStops = useRecentStops(port, !readOnly, i18n.t('busSearch.recentGroup'));
  const who = useCardTravelerSearch(ui, value.travelers, !readOnly);
  return (
    <div className="genui2 grid gap-3">
      {note && <div className="itin-intro">{renderMarkdown ? renderMarkdown(note) : note}</div>}
      <div className="rounded-xl border border-border bg-card p-3 shadow-(--shadow-elevation)">
        <BusSearchBar
          value={{ ...value, travelers: withPhotos(value.travelers, who.photos) }}
          copy={plannerCopy(i18n)}
          {...(who.search ? { travelerSearch: who.search } : {})}
          layout="inline"
          tripType={tripType}
          onTripTypeChange={setTripType}
          {...(recentStops ? { recentStops } : {})}
          {...stopDraftsOf(ui)}
          searchStops={async (term) => {
            const stops = port ? await port.searchStops(term) : [];
            return stops.map((stop) => ({
              id: stop.code,
              label: stop.name,
              icon: 'place' as const,
            }));
          }}
          today={today}
          busy={readOnly}
          guided={!superseded}
          guidedOpen={replay ? 'never' : 'always'}
          locale={i18n.locale}
          onChange={setValue}
          onSubmit={(next) => {
            if (!next.from || !next.to || !next.outwardDate) return;
            if (tripType === 'round_trip' && !next.returnDate) return;
            onSearch({
              ...travelersOf(next, ui.requesterId),
              from: next.from,
              to: next.to,
              outwardDate: next.outwardDate,
              ...(tripType === 'round_trip' && next.returnDate
                ? { returnDate: next.returnDate }
                : {}),
            });
          }}
        />
      </div>
    </div>
  );
}
